import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  CONSENT_TEXT_LANGUAGES,
  OPTIONAL_REQUIRED_FIELDS,
  type ConsentTextView,
  type OptionalRequiredField,
  type RegistrationSettingsView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  Dialog,
  DialogActions,
  FieldGroup,
  LoadingBlock,
  Notice,
  PageHeader,
  RadioGroup,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { formatEatDate } from '@wodi/domain';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import {
  adminConsentTextsQuery,
  adminRegistrationSettingsQuery,
  createConsentText,
  organisationKeys,
  retireConsentText,
  saveRegistrationSettings,
} from '../api/organisation.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { outsideDialogs, TableScroll } from '../layout/screen.tsx';
import styles from '../patients/patients.module.css';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Registration (docs/plans/phase1-registration.md §12, decisions 3, 6, 9, 17):
// the data-sharing consent wording read to patients, the provisional duplicate
// thresholds, the fields registration requires, and how often a patient view is audited.
// Wodi ships no consent wording: the organisation enters what it has approved
// (TODO(business): the approved English and Kiswahili wording, decision 3).

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const LANGUAGE_LABELS: Record<ConsentTextView['language'], string> = {
  en: 'English',
  sw: 'Kiswahili',
};

export const REQUIRED_FIELD_LABELS: Record<OptionalRequiredField, string> = {
  family_name: 'Family name',
  phone: 'Phone number',
  identifier: 'An identifier, such as a national ID',
  residence: 'Residence',
  next_of_kin: 'Next of kin',
};

function AddWording({ online, onDone }: { online: boolean; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [language, setLanguage] = useState<ConsentTextView['language']>('en');
  const [version, setVersion] = useState('');
  const [body, setBody] = useState('');
  const [tried, setTried] = useState(false);
  const valid = version.trim() !== '' && body.trim() !== '';
  const add = useMutation({
    mutationFn: () =>
      createConsentText({
        purpose: 'hie_data_sharing',
        language,
        version: version.trim(),
        body: body.trim(),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: organisationKeys.consentTexts });
      await queryClient.invalidateQueries({ queryKey: ['consent-texts'] });
      toasts.show({
        title: 'Consent wording saved',
        description: `${LANGUAGE_LABELS[created.language]}, version ${created.version}`,
      });
      onDone();
    },
  });
  const submit = () => {
    setTried(true);
    if (valid && online) add.mutate();
  };
  useShortcut('mod+enter', submit, {
    allowInFields: true,
    enabled: !add.isPending,
    description: 'Save wording',
  });
  return (
    <form
      className={styles.inlineForm}
      aria-label="Add consent wording"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <RadioGroup
        label="Language"
        orientation="horizontal"
        value={language}
        onChange={(v) => {
          setLanguage(v as ConsentTextView['language']);
        }}
        options={CONSENT_TEXT_LANGUAGES.map((l) => ({ id: l, label: LANGUAGE_LABELS[l] }))}
      />
      <TextField
        label="Version"
        description="Your own label, such as the date it was approved. Each patient's decision keeps the version they heard."
        width="sm"
        value={version}
        onChange={setVersion}
        {...(tried && version.trim() === '' ? { errorMessage: 'Enter a version.' } : {})}
      />
      <TextArea
        label="Wording"
        description="Exactly as reception reads it to the patient."
        rows={8}
        value={body}
        onChange={setBody}
        {...(tried && body.trim() === '' ? { errorMessage: 'Enter the wording.' } : {})}
      />
      {add.error === null ? null : (
        <Notice tone="error">{message(add.error, "The wording wasn't saved. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={add.isPending}
          pendingLabel="Saving…"
          shortcut="mod+enter"
        >
          Save wording
        </Button>
        <Button variant="quiet" onPress={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function ConsentWordingSection({ online }: { online: boolean }) {
  const queryClient = useQueryClient();
  const texts = useQuery(adminConsentTextsQuery);
  const [adding, setAdding] = useState(false);
  const [retiring, setRetiring] = useState<ConsentTextView | null>(null);
  const retire = useMutation({
    mutationFn: (text: ConsentTextView) => retireConsentText(text.id),
    onSuccess: async () => {
      setRetiring(null);
      await queryClient.invalidateQueries({ queryKey: organisationKeys.consentTexts });
      await queryClient.invalidateQueries({ queryKey: ['consent-texts'] });
    },
  });
  useShortcut(
    'n',
    outsideDialogs(() => {
      setAdding(true);
    }),
    { enabled: online && !adding && retiring === null, description: 'Add consent wording' },
  );
  const all = texts.data ?? [];
  return (
    <Section
      title="Data-sharing consent wording"
      description="What reception reads to a patient before recording whether they agree to share their data with the national health information exchange."
      {...(adding
        ? {}
        : {
            actions: (
              <Button
                isDisabled={!online}
                shortcut="n"
                onPress={() => {
                  setAdding(true);
                }}
              >
                Add wording
              </Button>
            ),
          })}
    >
      <Notice tone="info">
        Wodi doesn't supply this wording. Enter the English and Kiswahili wording your organisation
        has approved. A new version replaces the one in use for its language.
      </Notice>
      {adding ? (
        <AddWording
          online={online}
          onDone={() => {
            setAdding(false);
          }}
        />
      ) : null}
      {texts.isPending ? <LoadingBlock label="Loading consent wording" rows={2} /> : null}
      {texts.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void texts.refetch()}>
              Try again
            </Button>
          }
        >
          {message(texts.error, "The consent wording couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {texts.isSuccess
        ? CONSENT_TEXT_LANGUAGES.map((language) => {
            const active = all.find((t) => t.language === language && t.status === 'active');
            return (
              <div key={language} className={styles.inlineForm}>
                <h3 className={styles.meta}>
                  {active === undefined
                    ? `${LANGUAGE_LABELS[language]}: none in use`
                    : `${LANGUAGE_LABELS[language]}: version ${active.version}, added ${formatEatDate(new Date(active.createdAt))}`}
                </h3>
                {active === undefined ? (
                  <p>
                    {`No ${LANGUAGE_LABELS[language]} wording is in use.${language === 'en' ? ' Reception reads the English wording when there is one, so consent can’t be recorded until one is added.' : ''}`}
                  </p>
                ) : (
                  <>
                    <p className={styles.consentText}>{active.body}</p>
                    <div className={styles.actions}>
                      <Button
                        dense
                        variant="quiet"
                        isDisabled={!online}
                        aria-label={`Stop using the ${LANGUAGE_LABELS[language]} wording`}
                        onPress={() => {
                          setRetiring(active);
                        }}
                      >
                        Stop using
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          })
        : null}
      {all.some((t) => t.status === 'retired') ? (
        <TableScroll>
          <Table flat aria-label="Earlier consent wording">
            <TableHeader>
              <Column isRowHeader>Version</Column>
              <Column>Language</Column>
              <Column>Added</Column>
              <Column>Status</Column>
            </TableHeader>
            <TableBody items={all.filter((t) => t.status === 'retired')}>
              {(t) => (
                <Row id={t.id}>
                  <Cell>{t.version}</Cell>
                  <Cell>{LANGUAGE_LABELS[t.language]}</Cell>
                  <Cell>{formatEatDate(new Date(t.createdAt))}</Cell>
                  <Cell>
                    <Tag state="neutral">No longer used</Tag>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      ) : null}
      <Dialog
        title="Stop using this wording?"
        isOpen={retiring !== null}
        onOpenChange={(open) => {
          if (!open) setRetiring(null);
        }}
      >
        <p>
          {retiring === null
            ? ''
            : `Reception can't record consent in ${LANGUAGE_LABELS[retiring.language]} until new wording is added. Decisions already recorded keep this version.`}
        </p>
        {retire.error === null ? null : (
          <Notice tone="error">{message(retire.error, "It wasn't changed. Try again.")}</Notice>
        )}
        <DialogActions>
          <Button
            variant="quiet"
            onPress={() => {
              setRetiring(null);
            }}
          >
            Keep using it
          </Button>
          <Button
            variant="primary"
            isPending={retire.isPending}
            pendingLabel="Saving…"
            onPress={() => {
              if (retiring !== null) retire.mutate(retiring);
            }}
          >
            Stop using
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
}

interface SettingsDraft {
  nameSimilarityPercent: string;
  estimatedBirthDateToleranceYears: string;
  sharedPhoneRaisesCandidate: boolean;
  requiredFields: OptionalRequiredField[];
  patientViewAuditWindowMinutes: string;
}

function toDraft(view: RegistrationSettingsView): SettingsDraft {
  return {
    nameSimilarityPercent: String(view.duplicates.nameSimilarityPercent),
    estimatedBirthDateToleranceYears: String(view.duplicates.estimatedBirthDateToleranceYears),
    sharedPhoneRaisesCandidate: view.duplicates.sharedPhoneRaisesCandidate,
    requiredFields: [...view.requiredFields],
    patientViewAuditWindowMinutes: String(view.patientViewAuditWindowMinutes),
  };
}

/** A whole number within [min, max], or null. */
function wholeNumber(text: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(text.trim())) return null;
  const value = Number(text.trim());
  return value >= min && value <= max ? value : null;
}

function SettingsForm({ view, online }: { view: RegistrationSettingsView; online: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SettingsDraft>(() => toDraft(view));
  const similarity = wholeNumber(draft.nameSimilarityPercent, 1, 100);
  const tolerance = wholeNumber(draft.estimatedBirthDateToleranceYears, 0, 10);
  const auditWindow = wholeNumber(draft.patientViewAuditWindowMinutes, 0, 720);
  const valid = similarity !== null && tolerance !== null && auditWindow !== null;
  const save = useMutation({
    mutationFn: () =>
      saveRegistrationSettings({
        duplicates: {
          nameSimilarityPercent: similarity ?? 0,
          estimatedBirthDateToleranceYears: tolerance ?? 0,
          sharedPhoneRaisesCandidate: draft.sharedPhoneRaisesCandidate,
        },
        requiredFields: draft.requiredFields,
        patientViewAuditWindowMinutes: auditWindow ?? 0,
      }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(organisationKeys.registrationSettings, saved);
      await queryClient.invalidateQueries({ queryKey: ['registration-settings'] });
      setDraft(toDraft(saved));
      toasts.show({ title: 'Registration settings saved' });
    },
  });
  const toggleField = (field: OptionalRequiredField, on: boolean) => {
    setDraft((d) => ({
      ...d,
      requiredFields: on
        ? [...d.requiredFields, field]
        : d.requiredFields.filter((f) => f !== field),
    }));
  };
  return (
    <form
      aria-label="Registration settings"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && online) save.mutate();
      }}
    >
      <Section
        title="Registration checks"
        description="Possible duplicates are shown to reception before a new patient is saved. The thresholds are provisional until they are confirmed."
      >
        <div className={styles.row}>
          <TextField
            label="Names count as similar from"
            unit="%"
            inputMode="numeric"
            width="xs"
            value={draft.nameSimilarityPercent}
            onChange={(v) => {
              setDraft((d) => ({ ...d, nameSimilarityPercent: v }));
            }}
            {...(similarity === null
              ? { errorMessage: 'Enter a whole number from 1 to 100.' }
              : {})}
          />
          <TextField
            label="Estimated birth dates count as close within"
            unit="years"
            inputMode="numeric"
            width="xs"
            value={draft.estimatedBirthDateToleranceYears}
            onChange={(v) => {
              setDraft((d) => ({ ...d, estimatedBirthDateToleranceYears: v }));
            }}
            {...(tolerance === null ? { errorMessage: 'Enter a whole number from 0 to 10.' } : {})}
          />
        </div>
        <Checkbox
          isSelected={draft.sharedPhoneRaisesCandidate}
          onChange={(on) => {
            setDraft((d) => ({ ...d, sharedPhoneRaisesCandidate: on }));
          }}
        >
          A shared phone number alone shows a possible duplicate
        </Checkbox>
        <FieldGroup
          legend="Also required at registration"
          description="A given name, sex, and a date of birth or estimated age are always required."
        >
          {OPTIONAL_REQUIRED_FIELDS.map((field) => (
            <Checkbox
              key={field}
              isSelected={draft.requiredFields.includes(field)}
              onChange={(on) => {
                toggleField(field, on);
              }}
            >
              {REQUIRED_FIELD_LABELS[field]}
            </Checkbox>
          ))}
        </FieldGroup>
        <TextField
          label="Record a patient view once per session within"
          unit="minutes"
          description="0 records every view. Reads of a patient's record are always audited."
          inputMode="numeric"
          width="xs"
          value={draft.patientViewAuditWindowMinutes}
          onChange={(v) => {
            setDraft((d) => ({ ...d, patientViewAuditWindowMinutes: v }));
          }}
          {...(auditWindow === null ? { errorMessage: 'Enter a whole number from 0 to 720.' } : {})}
        />
        {save.error === null ? null : (
          <Notice tone="error">
            {message(save.error, "The settings weren't saved. Try again.")}
          </Notice>
        )}
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online || !valid}
            isPending={save.isPending}
            pendingLabel="Saving…"
          >
            Save registration settings
          </Button>
        </div>
      </Section>
    </form>
  );
}

export function RegistrationSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const allowed = session !== null && can(session, 'admin.organisation.manage');
  const settings = useQuery({ ...adminRegistrationSettingsQuery, enabled: allowed });

  if (!allowed) {
    return (
      <>
        <PageHeader title="Registration" />
        <Notice tone="info">
          You don't have permission to change organisation settings. Ask an administrator if you
          need it.
        </Notice>
      </>
    );
  }

  return (
    <SettingsPage title="Registration">
      {online ? null : (
        <Notice tone="info">You're offline. Changes can be saved once you reconnect.</Notice>
      )}
      <ConsentWordingSection online={online} />
      {settings.isPending ? <LoadingBlock label="Loading registration settings" rows={3} /> : null}
      {settings.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void settings.refetch()}>
              Try again
            </Button>
          }
        >
          {message(settings.error, "Registration settings couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {settings.data === undefined ? null : (
        <SettingsForm key={JSON.stringify(settings.data)} view={settings.data} online={online} />
      )}
    </SettingsPage>
  );
}
